import MarkdownIt from "markdown-it";
import React, { useEffect, useState, useRef } from "react";
import MdEditor from "react-markdown-editor-lite";

import "react-markdown-editor-lite/lib/index.css";
import { useDispatch } from "react-redux";
import {
  uploadContentFiles,
  setIsUploadImageError,
} from "reducers/companyContent";
import useCustomHook from "reducers/ContentCreateHook";

import { IMG_VALIDATE } from "../constants";

import MarkdownViewer from "./MarkdownViewer";

import CustomToast from "components/CustomToast";
import { useSnackbar } from "notistack";

// Markdown renderer
const mdParser = new MarkdownIt();

const CustomMarkdownEditor = ({
  value,
  onChangeText,
  error = false,
  errorText,
  ...props
}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { news } = useCustomHook();
  const [mode, setMode] = useState<"edit" | "preview">("edit");
  const normalizedValue = typeof value === "string" ? value.replace(/\r\n/g, "\n").replace(/\r/g, "\n") : "";
  const [isOver, setIsOver] = useState(false);
  const [errorState, setErrorState] = useState({ isError: false, message: "" });
  const uploadQueue = useRef(Promise.resolve());

  const editorRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (errorText) {
      setErrorState({ isError: error, message: errorText });
    } else {
      setErrorState({ isError: false, message: "" });
    }
  }, [errorText]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const handleNativePaste = (e: ClipboardEvent) => {
        const { clipboardData } = e;
        if (!clipboardData) return;

        let text = clipboardData.getData("text/plain");
        if (text) {
          e.preventDefault();
          e.stopImmediatePropagation();

          text = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
          const textarea = container.querySelector("textarea");
          if (textarea) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const currentVal = textarea.value;

            const newVal = currentVal.substring(0, start) + text + currentVal.substring(end);
            
            onChangeText(newVal);

            const newCursorPosition = start + text.length;
            setTimeout(() => {
              textarea.focus();
              textarea.setSelectionRange(newCursorPosition, newCursorPosition);
            }, 0);
          }
          return;
        }

        const { items } = clipboardData;
        if (items) {
          for (const item of items) {
            if (item.kind === "file" && item.type.startsWith("image/")) {
              return;
            }
          }
        }
      };

      container.addEventListener("paste", handleNativePaste, true);
      return () => {
        container.removeEventListener("paste", handleNativePaste, true);
      };
    }
  }, [normalizedValue, onChangeText])

  const handleDragOver = (e: any) => {
    e.preventDefault();
    setIsOver(true);
  };

  const handleDragLeave = (e: any) => {
    e.preventDefault();
    setIsOver(false);
  };

  const showCustomToast = ({
    title = "メッセージ",
    message,
    variant = "success",
    type = "normal",
  }) => {
    enqueueSnackbar("I love snacks.", {
      content: (key) => (
        <CustomToast
          closeSnackbar={closeSnackbar}
          message={message}
          snackbarKey={key}
          title={title}
          type={type}
          variant={variant}
        />
      ),
    });
  };

  const handleDrop = (e: any) => {
    e.preventDefault();
    setIsOver(false);

    const { files } = e.dataTransfer;
    if (!files.length) return;

    for (const file of files) {
      if (!file) continue;

      const isImage = file.type.startsWith("image/");
      const isValidFormat = ["image/png", "image/jpeg"].includes(file.type);

      if (isImage && !isValidFormat) {
        continue;
      }

      if (!isImage) {
        showCustomToast({message: "ファイル形式がPNG、JPG、JPEG形式ではありません。", variant: "error"});
        return;
      }
    }
  };

  const validateFile = async (file: File): Promise<boolean> => {
    setErrorState({
      isError: false,
      message: "",
    });

    if (file && !["image/png", "image/jpeg"].includes(file.type)) {
      showCustomToast({message: "ファイル形式がPNG、JPG、JPEG形式ではありません。", variant: "error"});
      return false;
    }

    if (file.size > IMG_VALIDATE.THUMB_MAX_FILE_SIZE) {
      showCustomToast({
        message:
          "選択した画像サイズは制限（8MB）を超えています。制限内の画像を指定してください。",
        variant: "error",
      });
      return false;
    }

    const imageUrl = URL.createObjectURL(file);

    const isValidResolution = await new Promise<boolean>((resolve) => {
      const img = new Image();

      img.addEventListener("load", () => {
        const valid =
          img.width <= IMG_VALIDATE.THUMB_MAX_WIDTH &&
          img.height <= IMG_VALIDATE.THUMB_MAX_HEIGHT;

        URL.revokeObjectURL(imageUrl);
        resolve(valid);
      });

      img.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        resolve(false);
      };

      img.src = imageUrl;
    });

    if (!isValidResolution) {
      showCustomToast({
        message:
          "解像度が 横 :1980px、 縦 :720px以内のファイルを選択してください。",
        variant: "error",
      });
      return false;
    }

    return true;
  };

  const processUpload = async (file: File) => {
    if (!file) return null;

    const isValid = await validateFile(file);
    if (!isValid) {
      dispatch(setIsUploadImageError(true));
      return null;
    }

    const formData = { files: [file] };

    const res = await dispatch(uploadContentFiles({ formData }));

    if (res?.payload?.success) {
      return res.payload.files[0]?.path;
    }
    return null;
  };

  const uploadImage = (file: File) => {
    uploadQueue.current = uploadQueue.current.then(() => processUpload(file));
    return uploadQueue.current;
  };

  return (
    <>
      <div
        ref={containerRef}
        className={`relative ${errorState.isError ? "border-2 border-[#F33D62] active:border-[#F33D62] focus:border-[#F33D62] rounded" : ""} ${isOver ? "is-dragging" : ""}`}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {mode === "edit" ? (
          <MdEditor
            {...props}
            ref={(el: any) => {
              editorRef.current = el;
              if (el && !el._isIntercepted) {
                el._isIntercepted = true;
                const originalInsertMarkdown = el.insertMarkdown;
                el.insertMarkdown = function (type: string, option: any) {
                  if (
                    ["bold", "italic", "underline", "strikethrough", "inlinecode", "link"].includes(
                      type
                    )
                  ) {
                    const textarea = el.getMdElement();
                    if (textarea) {
                      let start = textarea.selectionStart;
                      let end = textarea.selectionEnd;
                      const value = textarea.value;

                      // Trim leading whitespace/newlines
                      while (start < end && /\s/.test(value.charAt(start))) {
                        start++;
                      }

                      // Trim trailing whitespace/newlines
                      while (end > start && /\s/.test(value.charAt(end - 1))) {
                        end--;
                      }

                      textarea.setSelectionRange(start, end);
                    }
                  }
                  return originalInsertMarkdown.call(el, type, option);
                };
              }
            }}
            allowPasteImage
            canView={{
              hideMenu: false,
              fullScreen: false,
            }}
            imageAccept={["image/png", "image/jpeg"]}
            markdownClass="custom-scrollbar"
            plugins={[
              "header",
              "font-bold",
              "font-italic",
              "font-underline",
              "divider",
              "font-strikethrough",
              "list-unordered",
              "list-ordered",
              "divider",
              "block-quote",
              "block-code-inline",
              "block-code-block",
              "divider",
              "image",
              "link",
              "mode-toggle",
              "full-screen",
            ]}
            renderHTML={(text) => mdParser.render(text)}
            value={normalizedValue}
            view={{
              md: true,
              html: false,
            }}
            onChange={({ html, text }) => onChangeText(text)}
            onImageUpload={uploadImage}
          />
        ) : (
          <div className="border border-[#E0E0E0] rounded">
            <div className="min-h-[2.375rem] bg-[#f6f8fa] border-b border-[#E0E0E0] flex items-center px-2" />
            <div
              className="custom-html-style p-3 overflow-auto custom-scrollbar resize-y h-[15rem]"
              style={{
                resize: "vertical",
              }}
            >
              <MarkdownViewer content={normalizedValue} />
            </div>
          </div>
        )}
        <div className="flex absolute top-0 left-0 h-[2.375rem]">
          <button
            className={`h-[2.4375rem] rounded-t px-3 text-sm flex items-center justify-center ${mode === "edit" ? "bg-white border border-[#E0E0E0] border-b border-b-[#FFF] text-[#FF861A]" : "border-0 alu-text-base-2"}`}
            onClick={() => setMode("edit")}
          >
            編集
          </button>
          <button
            className={`h-[2.4375rem] rounded-t px-3 text-sm flex items-center justify-center ${mode === "preview" ? "bg-white border border-[#E0E0E0] border-b border-b-[#FFF] text-[#FF861A]" : "border-0 alu-text-base-2"}`}
            onClick={() => setMode("preview")}
          >
            プレビュー
          </button>
        </div>
      </div>
      {errorState.isError && (
        <p className="alu-text-error text-sm mt-2">
          {errorState.message ? `*${errorState.message}` : ""}
        </p>
      )}
    </>
  );
};

export default CustomMarkdownEditor;
